'use client';

import React, { useEffect, useState, useRef } from 'react';
import { StoreContext } from './index';
import {
  getSessionStorageItem,
  getStringSessionStorageItem,
  SessionStorageKey,
  setSessionStorageItem,
  updateSessionObjectStorageItem,
} from '../storage/session_storage';
import { UserChatAnswers } from '../chat/answers/chat_answers';
import { ProductLine, SourceType } from '../helpers/home_services.helper';
import { createUserOnPageLoading } from '../services/user_creation';

const DEFAULT_SESSION_ANSWERS = {
  [ProductLine.Windows]: {},
};

export default function StoreProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const [answerAttrs, setAnswerAttrs] = useState<UserChatAnswers>(
    DEFAULT_SESSION_ANSWERS
  );
  const [userPublicId, setUserPublicId] = useState<string | undefined>(
    undefined
  );
  const [sourceId, setSourceId] = useState<string | undefined>(undefined);
  const [sourceName, setSourceName] = useState<string | undefined>(undefined);
  const [campaignId, setCampaignId] = useState<string | undefined>(undefined);
  const [sourceType, setSourceType] = useState<SourceType | undefined>(
    undefined
  );
  const [zipCodeByIp, setZipCodeByIp] = useState<string | undefined>(undefined);
  const [isLoadingInitialData, setLoadingInitialData] = useState<boolean>(true);
  const hasCreatedUser = useRef(false);

  useEffect(() => {
    const userPublicId = getStringSessionStorageItem(
      SessionStorageKey.userPublicId
    );

    if (userPublicId != null) {
      setUserPublicId(userPublicId);
      return;
    }

    // Immediately block duplicate creation
    if (hasCreatedUser.current) return;
    hasCreatedUser.current = true;

    async function initUser() {
      setLoadingInitialData(true);
      const user = await createUserOnPageLoading();
      setUserPublicId(user.publicId);
      setZipCodeByIp(user?.geoData?.zipCode);
      setSourceId(user.sourceId);
      setSourceName(user.sourceName);
      setCampaignId(user.campaignId);
      setSourceType(user.sourceType);
      setLoadingInitialData(false);
      setSessionStorageItem(SessionStorageKey.userPublicId, user.publicId);
    }

    initUser();
  }, []);

  const setAnswerAttrsCallback = (
    productLine: ProductLine,
    answers: UserChatAnswers
  ): void => {
    const newProductAnswers = { ...answerAttrs[productLine], ...answers };
    const newAnswers = { ...answerAttrs, [productLine]: newProductAnswers };
    setAnswerAttrs(newAnswers);
    updateSessionObjectStorageItem(SessionStorageKey.userData, {
      answerAttrs: newAnswers,
    });
  };

  return (
    <StoreContext.Provider
      value={{
        userPublicId,
        setUserPublicId,
        sourceId,
        setSourceId,
        isLoadingInitialServerData: isLoadingInitialData,
        sourceType,
        setSourceType,
        answerAttrs,
        setAnswerAttrsCallback,
        campaignId,
        sourceName,
        zipCodeByIp,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
}
